import type { ReactNode } from "react";
import { Button } from "./Button";
import { ConfirmDialog, type ConfirmRefusal } from "./ConfirmDialog";
import { PencilIcon, RotateIcon, TrashIcon } from "./Icon";
import { LinkButton } from "./LinkButton";
import styles from "./RowActions.module.css";

type RowActionsProps = {
  editHref: string;
  isInactive: boolean;
  // "Archive" for skills, "Deactivate" for topics and tags — the wording
  // matches the column each one writes, so the button never overstates itself.
  retireLabel: string;
  retirePendingLabel: string;
  confirmTitle: string;
  confirmBody: ReactNode;
  // May come back refused: retiring a row can be blocked by rules that read
  // other tables, and the dialog reports that in place.
  retireAction: () => Promise<ConfirmRefusal>;
  // Listing-specific verbs sit before the shared ones. Only tags have one:
  // merging is possible there because nothing holds a foreign key to a tag.
  extraActions?: ReactNode;
  restoreAction: () => Promise<void>;
  // Offered wherever the caller passes it, independently of the retire state:
  // skills, traits and topics only allow it once retired, because archiving is
  // what stops new bindings appearing, while a never-published template has
  // nothing pointing at it and can be discarded straight from draft. Rows that
  // must never go — anything ever published — simply omit it. The action may
  // still refuse; every check re-runs in the query.
  deleteLabel?: string;
  deletePendingLabel?: string;
  deleteConfirmTitle?: string;
  deleteConfirmBody?: ReactNode;
  deleteAction?: () => Promise<ConfirmRefusal>;
  // What `revalidatePath` used to do from inside the action. The listing owns
  // its rows now, so a write that changed them has to say so.
  onCompleted: () => void;
};

export const RowActions = ({
  editHref,
  isInactive,
  retireLabel,
  retirePendingLabel,
  confirmTitle,
  confirmBody,
  retireAction,
  restoreAction,
  extraActions,
  deleteLabel = "Delete",
  deletePendingLabel = "Deleting...",
  deleteConfirmTitle,
  deleteConfirmBody,
  deleteAction,
  onCompleted,
}: RowActionsProps) => {
  // A refusal leaves the rows as they were, so only an accepted write asks the
  // listing to read again.
  const refreshUnlessRefused = async (
    run: () => Promise<ConfirmRefusal>,
  ): Promise<ConfirmRefusal> => {
    const refusal = await run();

    if (!refusal) {
      onCompleted();
    }

    return refusal;
  };

  return (
    <div className={styles.actions}>
      {extraActions}
      <LinkButton href={editHref} variant="ghost" shouldReplaceHistory>
        <PencilIcon size={14} />
        Edit
      </LinkButton>
      {isInactive ? (
        // Restoring puts a row back into use; nothing is lost by it, so it asks
        // no question.
        <form
          action={async () => {
            await restoreAction();
            onCompleted();
          }}
        >
          <Button type="submit" variant="ghost">
            <RotateIcon size={14} />
            Restore
          </Button>
        </form>
      ) : (
        <ConfirmDialog
          triggerLabel={retireLabel}
          title={confirmTitle}
          body={confirmBody}
          confirmLabel={retireLabel}
          pendingLabel={retirePendingLabel}
          isDestructive
          confirmAction={() => refreshUnlessRefused(retireAction)}
        />
      )}
      {deleteAction === undefined ? null : (
        <ConfirmDialog
          triggerLabel={deleteLabel}
          triggerIcon={<TrashIcon size={14} />}
          title={deleteConfirmTitle ?? `${deleteLabel} this record?`}
          body={deleteConfirmBody}
          confirmLabel={deleteLabel}
          pendingLabel={deletePendingLabel}
          isDestructive
          confirmAction={() => refreshUnlessRefused(deleteAction)}
        />
      )}
    </div>
  );
};
